import classNames from "classnames";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import TooltipIcon from "PFComponents/tooltip_icon/tooltip_icon";
import CloseIcon from "PFIcons/close.svg";
import FilterCleanIcon from "PFIcons/filter-clean.svg";
import HistoryIcon from "PFIcons/history.svg";
import { PageTarget } from "PFTypes/saved_filters";
import React, { JSX, useRef } from "react";
import { useTranslation } from "react-i18next";

import { SaveAsView } from "../save_as_view";
import { useSavedViewsContext } from "../saved_views_context/saved_views_context";
import css from "./filters_box.module.scss";
import { AccordionProps } from "./filters_box.types";
import { FiltersBoxContent } from "./filters_box_content";
import { FiltersBaseProps } from "./filters_element";

export type FiltersBoxProps = {
  handleFiltersChange?: (filters: any) => void;
  clearFilters?: (params?: any) => void;
  closeFilters?: () => void;
  blockedList?: string[];
  filtersAreClear?: boolean;
  allowToClose?: boolean;
  accordionProps?: AccordionProps;
  style?: React.CSSProperties;
  isLoading?: boolean;
  viewsKey?: PageTarget | null;
  withTitle?: boolean;
  classes?: {
    root?: string;
    header?: string;
    list?: string;
  };
  letRestoreAll?: boolean;
  restoreAllTooltipContent?: React.ReactNode;
  isRelevantToMeDisabled?: boolean;
  portalRef?: React.RefObject<HTMLDivElement>;
} & FiltersBaseProps;

const FiltersBox = (props: React.PropsWithChildren<FiltersBoxProps>): JSX.Element => {
  const {
    meta,
    handleFiltersChange,
    clearFilters,
    closeFilters,
    filtersAreClear,
    allowToClose = true,
    style,
    isLoading,
    viewsKey = null,
    withTitle = true,
    classes = {},
    letRestoreAll,
    restoreAllTooltipContent,
    isRelevantToMeDisabled
  } = props;

  const lastFocusedFilterNameRef = useRef(null);
  const { t } = useTranslation();
  const { setSelectedViewId } = useSavedViewsContext(viewsKey);

  return (
    <SectionBox style={style} isLoading={isLoading} className={classNames(css.root, classes.root)}>
      {allowToClose && (
        <button className={css.close} onClick={closeFilters}>
          <CloseIcon width={20} height={20} />
        </button>
      )}

      <div className={classNames(css.header, classes.header)}>
        {withTitle && <strong>{t("filters.filters")}</strong>}
        {!filtersAreClear && (
          <Button
            className={css.actionAllButton}
            displayHover={false}
            kind="text"
            onClick={() => {
              if (lastFocusedFilterNameRef.current) {
                lastFocusedFilterNameRef.current = null;
              }
              setSelectedViewId(null);
              clearFilters && clearFilters();
            }}
          >
            <FilterCleanIcon height={16} width={16} />
            <span>{t("filters.clearAll")}</span>
          </Button>
        )}
        {letRestoreAll && (
          <>
            <Button
              className={css.actionAllButton}
              displayHover={false}
              kind="text"
              onClick={() => {
                clearFilters?.(meta?.default_filters);
              }}
            >
              <HistoryIcon height={16} width={16} />
              <span>{t("filters.restoreAll")}</span>
            </Button>
            {restoreAllTooltipContent && (
              <TooltipIcon
                content={restoreAllTooltipContent}
                IconComponent={(props) => <Icon name="info" {...props} />}
                width={20}
                height={20}
              />
            )}
          </>
        )}
      </div>
      <FiltersBoxContent {...props} lastFocusedFilterNameRef={lastFocusedFilterNameRef} />
      {viewsKey && (
        <SaveAsView
          meta={meta}
          clearFilters={handleFiltersChange}
          viewsKey={viewsKey}
          isRelevantToMeDisabled={isRelevantToMeDisabled}
        />
      )}

      {allowToClose && (
        <div className={css.footer}>
          <Button onClick={closeFilters}>{t("done")}</Button>
        </div>
      )}
    </SectionBox>
  );
};

export default FiltersBox;
